import {
  getMultiResumeLegalLinks,
  isMultiResumeEnglishLocale,
  isMultiResumeTraditionalChineseLocale,
  multiResumeBrand,
  multiResumeLegalLinks,
} from "@/utils/multiresume-brand";

import {
  PHASE4_TRUST_COPY_VERSION,
  PHASE6_AI_BASE_URL,
  PHASE6_AI_MODEL,
  PHASE6_AI_PROVIDER_ENTITY,
  PHASE6_AI_PROVIDER_ENTITY_ZH,
  PHASE6_LEGAL_EFFECTIVE_DATE,
  PHASE6_LEGAL_POLICY_VERSION,
  PHASE6_MINIMUM_AGE,
} from "./trust-config";

export type Phase4DisclosureSurface =
  | "registration"
  | "pdfUpload"
  | "firstAiUse"
  | "purchasePreflight"
  | "quotaPaywall";

export type Phase4TrustLink = {
  label: string;
  href: string;
};

export type Phase4DisclosureCopy = {
  id: string;
  version: typeof PHASE4_TRUST_COPY_VERSION;
  surface: Phase4DisclosureSurface;
  title: string;
  body: string;
  bullets: string[];
  requiredAcknowledgements: string[];
  links: Phase4TrustLink[];
};

export type Phase4TrustDocumentKey =
  | "terms"
  | "privacy"
  | "acceptableUsePolicy"
  | "support"
  | "license"
  | "aiService"
  | "crossBorderProcessing"
  | "refundPolicy"
  | "betaStatus";

export type Phase4TrustDocumentSection = {
  title: string;
  id?: string;
  headingId?: string;
  paragraphs?: string[];
  bullets?: string[];
  links?: Phase4TrustLink[];
};

export type Phase4TrustDocument = {
  title: string;
  description: string;
  sections: Phase4TrustDocumentSection[];
};

export type Phase6LegalMetadata = {
  controllerLabel: string;
  controller: string;
  addressLabel: string;
  address: string;
  effectiveDateLabel: string;
  effectiveDate: string;
  versionLabel: string;
  version: string;
  minimumAgeLabel: string;
  minimumAge: string;
  supportLabel: string;
  support: string;
  privacyLabel: string;
  privacy: string;
};

const phase6LegalMetadataZhCn: Phase6LegalMetadata = {
  controllerLabel: "运营者 / 个人信息处理负责人",
  controller: "Xi Guanwen",
  addressLabel: "公开地址",
  address: "中国上海市宝山区南大路38弄9支弄14-201",
  effectiveDateLabel: "生效日期",
  effectiveDate: PHASE6_LEGAL_EFFECTIVE_DATE,
  versionLabel: "政策版本",
  version: PHASE6_LEGAL_POLICY_VERSION,
  minimumAgeLabel: "最低年龄",
  minimumAge: `${PHASE6_MINIMUM_AGE}+`,
  supportLabel: "支持邮箱",
  support: multiResumeBrand.supportEmail,
  privacyLabel: "隐私请求邮箱",
  privacy: multiResumeBrand.privacyEmail,
};

const phase6LegalMetadataEn: Phase6LegalMetadata = {
  controllerLabel: "Operator / personal information controller",
  controller: "Xi Guanwen",
  addressLabel: "Public address",
  address: multiResumeBrand.legalAddress,
  effectiveDateLabel: "Effective date",
  effectiveDate: PHASE6_LEGAL_EFFECTIVE_DATE,
  versionLabel: "Policy version",
  version: PHASE6_LEGAL_POLICY_VERSION,
  minimumAgeLabel: "Minimum age",
  minimumAge: `${PHASE6_MINIMUM_AGE}+`,
  supportLabel: "Support email",
  support: multiResumeBrand.supportEmail,
  privacyLabel: "Privacy request email",
  privacy: multiResumeBrand.privacyEmail,
};

export function getPhase6LegalMetadata() {
  if (isMultiResumeEnglishLocale()) return phase6LegalMetadataEn;
  if (isMultiResumeTraditionalChineseLocale()) {
    return {
      ...phase6LegalMetadataZhCn,
      controllerLabel: "營運者／個人資料處理負責人",
      addressLabel: "公開地址",
      effectiveDateLabel: "生效日期",
      versionLabel: "政策版本",
      minimumAgeLabel: "最低年齡",
      supportLabel: "支援信箱",
      privacyLabel: "隱私請求信箱",
    };
  }
  return phase6LegalMetadataZhCn;
}

export const phase6ProviderDisclosure = {
  entity: {
    en: PHASE6_AI_PROVIDER_ENTITY,
    zh: PHASE6_AI_PROVIDER_ENTITY_ZH,
  },
  model: PHASE6_AI_MODEL,
  baseUrl: PHASE6_AI_BASE_URL,
} as const;

type Phase4LandingCopy = {
  title: string;
  subtitle: string;
  primaryCta: string;
  dataNoticeCta: string;
  signInCta: string;
  dashboardCta: string;
  betaSummaryTitle: string;
  betaSummaryIntro: string;
  betaBullets: ReadonlyArray<{ id: string; marker: string; text: string }>;
  workflowTitle: string;
  stepLabel: string;
  workflow: ReadonlyArray<{ title: string; body: string }>;
  footnoteAriaLabel: string;
  footnoteLinkLabel: string;
  footnotes: ReadonlyArray<{ id: string; marker: string; body: string }>;
};

export const phase4TrustLinks: Phase4TrustLink[] = multiResumeLegalLinks.map(({ label, href }) => ({ label, href }));

const phase4TrustLinksEn: Phase4TrustLink[] = [
  { label: "Pricing", href: "/pricing" },
  { label: "Terms of Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Acceptable Use Policy", href: "/acceptable-use-policy" },
  { label: "Refund Policy", href: "/refund-policy" },
  { label: "Support", href: "/support" },
  { label: "AI Service Notice", href: "/ai-service" },
  { label: "Cross-border Processing", href: "/cross-border-processing" },
  { label: "License", href: "/license" },
];

export function getPhase4TrustLinks(): Phase4TrustLink[] {
  return getMultiResumeLegalLinks().map(({ label, href }) => ({ label, href }));
}

const termsLink = { label: "用户协议", href: "/terms" };
const privacyLink = { label: "隐私政策", href: "/privacy" };
const acceptableUseLink = { label: "可接受使用政策", href: "/acceptable-use-policy" };
const aiServiceLink = { label: "AI 服务说明", href: "/ai-service" };
const crossBorderLink = { label: "跨境数据处理说明", href: "/cross-border-processing" };
const refundLink = { label: "退款规则", href: "/refund-policy" };
const supportLink = { label: "支持", href: "/support" };

const privacyLinkZhTw = { label: "隱私政策", href: "/privacy" };
const aiServiceLinkZhTw = { label: "AI 服務說明", href: "/ai-service" };
const crossBorderLinkZhTw = { label: "跨境資料處理說明", href: "/cross-border-processing" };
const supportLinkZhTw = { label: "支援", href: "/support" };

const termsLinkEn = { label: "Terms of Service", href: "/terms" };
const privacyLinkEn = { label: "Privacy Policy", href: "/privacy" };
const acceptableUseLinkEn = { label: "Acceptable Use Policy", href: "/acceptable-use-policy" };
const aiServiceLinkEn = { label: "AI Service Notice", href: "/ai-service" };
const crossBorderLinkEn = { label: "Cross-border Data Processing", href: "/cross-border-processing" };
const refundLinkEn = { label: "Refund Policy", href: "/refund-policy" };
const supportLinkEn = { label: "Support", href: "/support" };

export const phase4Disclosures: Record<Phase4DisclosureSurface, Phase4DisclosureCopy> = {
  registration: {
    id: "phase4.registration_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "registration",
    title: "注册前请确认 beta 边界",
    body: "MultiResume 当前是邀请制 beta。服务通过境外基础设施提供，AI 请求会发送给当前配置的第三方 provider。符合资格的付费购买如开启，将通过 Waffo Pancake 提供安全 checkout。",
    bullets: [
      "请先阅读用户协议、隐私政策、可接受使用政策、AI 服务说明和跨境数据处理说明。",
      "购买能力只对白名单账号开放，未受邀账号不会进入真实购买路径。",
      "AI provider 服务主体、模型、处理区域、retention / training 口径以实际 provider 条款和当前配置为准。",
    ],
    requiredAcknowledgements: ["terms_privacy", "ai_cross_border_processing"],
    links: [termsLink, privacyLink, acceptableUseLink, aiServiceLink, crossBorderLink, supportLink],
  },
  pdfUpload: {
    id: "phase4.pdf_upload_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "pdfUpload",
    title: "上传 PDF 前请理解处理方式",
    body: "上传后，MultiResume 会提取 PDF 文本并尝试转换为结构化简历。为完成解析和后续 AI 建议，简历内容可能进入 AI 和相关服务商处理链路。",
    bullets: ["请确认你有权上传该文件。", "AI 解析可能出错，请在保存前检查结果。"],
    requiredAcknowledgements: ["pdf_processing", "ai_cross_border_processing"],
    links: [privacyLink, aiServiceLink, crossBorderLink, supportLink],
  },
  firstAiUse: {
    id: "phase4.first_ai_use_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "firstAiUse",
    title: "AI 是建议工具",
    body: "AI 会基于你的简历、JD、当前编辑内容和用户指令生成建议。AI 输出可能不准确、不完整，或不适合你的真实经历。",
    bullets: [
      "请逐项审核、编辑、确认后再应用 AI 内容。",
      "默认不应改写公司、学校、日期、证书、头衔等硬事实。",
      "MultiResume 不保证面试、录用、涨薪、ATS 通过或任何求职结果。",
      "Provider entity、model、region、retention / training 以实际 provider 条款和当前配置为准。",
    ],
    requiredAcknowledgements: ["ai_suggestion_review"],
    links: [aiServiceLink, privacyLink, crossBorderLink, supportLink],
  },
  purchasePreflight: {
    id: "phase4.purchase_preflight_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "purchasePreflight",
    title: "购买前请确认 quota 和支付边界",
    body: "你将购买一次性 AI quota 包。它不是订阅，不会自动续费。符合资格的购买会通过 Waffo Pancake 提供安全 checkout，卡信息和支付方式明细由该 checkout 处理。",
    bullets: [
      "购买前会展示实际 package 名称、价格、币种、quota 数量、有效期和账号购买限制。",
      "AI quota 用于 PDF AI 解析、整体 AI 检查、section/item AI 优化、master PDF 合并等 AI 操作。",
      "普通编辑和本地预览不消耗 quota。",
      "退款和支持在 beta 阶段采用人工处理，不写成自动退款保证。",
    ],
    requiredAcknowledgements: ["purchase_terms", "refund_ai_cross_border_processing"],
    links: [refundLink, aiServiceLink, crossBorderLink, privacyLink, acceptableUseLink, supportLink],
  },
  quotaPaywall: {
    id: "phase4.quota_paywall_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "quotaPaywall",
    title: "AI quota 不足",
    body: "当前 AI 操作需要消耗 quota。购买能力只对白名单账号开放，非白名单账号仍可继续普通编辑和导出。",
    bullets: [
      "真实购买入口只有在账号资格和 beta review 通过后才会开放。",
      "前端 checkout 完成不能直接代表 quota 到账。",
      "服务端确认 payment completed 后才可发放 quota。",
    ],
    requiredAcknowledgements: [],
    links: [refundLink, supportLink, aiServiceLink, crossBorderLink],
  },
};

const phase4DisclosuresEn: Record<Phase4DisclosureSurface, Phase4DisclosureCopy> = {
  registration: {
    id: "phase4.registration_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "registration",
    title: "Confirm the beta boundaries before registration",
    body: "MultiResume is currently an invite-only beta. The service is provided through overseas infrastructure. AI requests are sent to the currently configured third-party provider. Eligible purchases, when enabled, use the configured Waffo Pancake test checkout boundary; live availability and account approval remain pending external review.",
    bullets: [
      "Please review the Terms of Service, Privacy Policy, Acceptable Use Policy, AI Service Notice, and Cross-border Data Processing notice first.",
      "Purchase access is limited to whitelisted accounts. Uninvited accounts will not enter a real purchase path.",
      "AI provider service entity, model, processing region, retention, and training posture depend on the actual provider terms and current configuration.",
    ],
    requiredAcknowledgements: ["terms_privacy", "ai_cross_border_processing"],
    links: [termsLinkEn, privacyLinkEn, acceptableUseLinkEn, aiServiceLinkEn, crossBorderLinkEn, supportLinkEn],
  },
  pdfUpload: {
    id: "phase4.pdf_upload_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "pdfUpload",
    title: "Understand processing before uploading a PDF",
    body: "After upload, MultiResume extracts PDF text and attempts to convert it into a structured resume. Resume content may enter the AI and related provider processing chain for parsing and later AI suggestions.",
    bullets: [
      "Confirm that you have the right to upload this file.",
      "AI parsing can be wrong. Review the result before saving.",
    ],
    requiredAcknowledgements: ["pdf_processing", "ai_cross_border_processing"],
    links: [privacyLinkEn, aiServiceLinkEn, crossBorderLinkEn, supportLinkEn],
  },
  firstAiUse: {
    id: "phase4.first_ai_use_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "firstAiUse",
    title: "AI is a suggestion tool",
    body: "AI generates suggestions from your resume, job description, current edits, and instructions. AI output can be inaccurate, incomplete, or unsuitable for your real experience.",
    bullets: [
      "Review, edit, and confirm each item before applying AI content.",
      "By default, hard facts such as companies, schools, dates, credentials, and titles should not be rewritten.",
      "MultiResume does not promise interviews, offers, salary increases, ATS outcomes, or any job-search result.",
      "Provider entity, model, region, retention, and training posture depend on the actual provider terms and current configuration.",
    ],
    requiredAcknowledgements: ["ai_suggestion_review"],
    links: [aiServiceLinkEn, privacyLinkEn, crossBorderLinkEn, supportLinkEn],
  },
  purchasePreflight: {
    id: "phase4.purchase_preflight_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "purchasePreflight",
    title: "Confirm quota and payment boundaries before purchase",
    body: "You are purchasing a one-time AI quota package. It is not a subscription and does not renew automatically. Eligible purchases use the configured Waffo Pancake checkout boundary when available; live availability and account approval remain pending external review, and card or payment method details are handled in that checkout flow.",
    bullets: [
      "Before purchase, the app shows the actual package name, price, currency, quota amount, validity period, and account purchase limit.",
      "AI quota is used for PDF AI parsing, whole-resume AI checks, section/item AI optimization, master PDF merge, and similar AI actions.",
      "Regular editing and local preview do not consume quota.",
      "Refunds and support are handled manually during beta and are not presented as an automatic refund promise.",
    ],
    requiredAcknowledgements: ["purchase_terms", "refund_ai_cross_border_processing"],
    links: [refundLinkEn, aiServiceLinkEn, crossBorderLinkEn, privacyLinkEn, acceptableUseLinkEn, supportLinkEn],
  },
  quotaPaywall: {
    id: "phase4.quota_paywall_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "quotaPaywall",
    title: "AI quota is insufficient",
    body: "This AI action requires quota. Purchase access is limited to whitelisted accounts. Non-whitelisted accounts can continue regular editing and export.",
    bullets: [
      "The real purchase entry opens only after account eligibility and beta review pass.",
      "A completed frontend checkout does not by itself mean quota has been granted.",
      "Quota is granted only after the server confirms payment completion.",
    ],
    requiredAcknowledgements: [],
    links: [refundLinkEn, supportLinkEn, aiServiceLinkEn, crossBorderLinkEn],
  },
};

const phase4DisclosuresZhTw: Record<Phase4DisclosureSurface, Phase4DisclosureCopy> = {
  ...phase4Disclosures,
  pdfUpload: {
    id: "phase4.pdf_upload_disclosure.v1",
    version: PHASE4_TRUST_COPY_VERSION,
    surface: "pdfUpload",
    title: "上傳 PDF 前請理解處理方式",
    body: "上傳後，MultiResume 會提取 PDF 文字並嘗試轉換為結構化履歷。為完成解析和後續 AI 建議，履歷內容可能進入 AI 與相關服務提供者的處理流程。",
    bullets: ["請確認你有權上傳此檔案。", "AI 解析可能出錯，請在儲存前檢查結果。"],
    requiredAcknowledgements: ["pdf_processing", "ai_cross_border_processing"],
    links: [privacyLinkZhTw, aiServiceLinkZhTw, crossBorderLinkZhTw, supportLinkZhTw],
  },
};

export const phase4LandingCopy = {
  title: "为每个目标岗位维护更合适的简历版本。",
  subtitle: "先建立一份 master resume，再为不同 JD 创建 target resume。AI 会给出优化建议，你审核后再决定是否应用。",
  primaryCta: "使用邀请账号开始",
  dataNoticeCta: "查看数据与 AI 说明",
  signInCta: "登录",
  dashboardCta: "进入工作台",
  betaSummaryTitle: "Beta 阶段使用说明",
  betaSummaryIntro: "当前仅限部分受邀账号访问。",
  betaBullets: [
    { id: "1", marker: "1", text: "需要邀请登录；如需申请访问，请联系管理员。" },
    { id: "2", marker: "2", text: "AI 服务可能不稳定，仅供建议，需你审核。" },
    { id: "3", marker: "3", text: "可能使用跨境基础设施，请自行评估后使用。" },
    { id: "4", marker: "4", text: "试用点数用完后，购买 package 需进一步审核；请联系管理员。" },
  ],
  workflowTitle: "从一份 master resume 到每个岗位的 target resume",
  stepLabel: "第 {step} 步",
  workflow: [
    {
      title: "建立 master resume",
      body: "上传 PDF 或创建空白 master，保留完整经历事实。",
    },
    {
      title: "粘贴 JD 创建 target",
      body: "每个 target 绑定一个目标岗位，围绕 JD 做差异化优化。",
    },
    {
      title: "审核 AI 建议后应用",
      body: "AI 只提供建议，不直接覆盖硬事实；用户逐项确认。",
    },
  ],
  footnoteAriaLabel: "Beta 摘要脚注",
  footnoteLinkLabel: "查看脚注",
  footnotes: [
    {
      id: "1",
      marker: "1",
      body: "当前为受控 beta，仅开放给部分受邀账号；账号访问、购买资格和使用规模会被限制。",
    },
    {
      id: "2",
      marker: "2",
      body: "AI 输出可能不准确、不完整或不适合你的经历；请在应用前自行审核。",
    },
    {
      id: "3",
      marker: "3",
      body: "PDF、简历、JD、AI 请求、支付和支持记录可能经由境外或第三方基础设施处理；请确认你接受后再使用。",
    },
    {
      id: "4",
      marker: "4",
      body: "免费或试用 AI 点数用完后，购买一次性 package 仍需账号白名单和人工审核；如需开通或确认资格，请联系管理员。",
    },
  ],
} as const satisfies Phase4LandingCopy;

const phase4LandingCopyZhTw = {
  title: "為每個目標職缺維護更合適的履歷版本。",
  subtitle: "先建立一份 master resume，再為不同 JD 建立 target resume。AI 會提出優化建議，你審核後再決定是否套用。",
  primaryCta: "使用邀請帳號開始",
  dataNoticeCta: "查看資料與 AI 說明",
  signInCta: "登入",
  dashboardCta: "進入工作台",
  betaSummaryTitle: "Beta 階段使用說明",
  betaSummaryIntro: "目前僅限部分受邀帳號存取。",
  betaBullets: [
    { id: "1", marker: "1", text: "需要邀請登入；如需申請存取，請聯絡管理員。" },
    { id: "2", marker: "2", text: "AI 服務可能不穩定，僅供建議，需你審核。" },
    { id: "3", marker: "3", text: "可能使用跨境基礎設施，請自行評估後使用。" },
    { id: "4", marker: "4", text: "試用點數用完後，購買 package 需進一步審核；請聯絡管理員。" },
  ],
  workflowTitle: "從一份 master resume 到每個職缺的 target resume",
  stepLabel: "第 {step} 步",
  workflow: [
    {
      title: "建立 master resume",
      body: "上傳 PDF 或建立空白 master，保留完整經歷事實。",
    },
    {
      title: "貼上 JD 建立 target",
      body: "每個 target 綁定一個目標職缺，圍繞 JD 做差異化優化。",
    },
    {
      title: "審核 AI 建議後套用",
      body: "AI 只提供建議，不直接覆蓋硬事實；使用者逐項確認。",
    },
  ],
  footnoteAriaLabel: "Beta 摘要註記",
  footnoteLinkLabel: "查看註記",
  footnotes: [
    {
      id: "1",
      marker: "1",
      body: "目前為受控 beta，僅開放給部分受邀帳號；帳號存取、購買資格和使用規模會被限制。",
    },
    {
      id: "2",
      marker: "2",
      body: "AI 輸出可能不準確、不完整或不適合你的經歷；請在套用前自行審核。",
    },
    {
      id: "3",
      marker: "3",
      body: "PDF、履歷、JD、AI 請求、支付和支援記錄可能經由境外或第三方基礎設施處理；請確認你接受後再使用。",
    },
    {
      id: "4",
      marker: "4",
      body: "免費或試用 AI 點數用完後，購買一次性 package 仍需帳號白名單和人工審核；如需開通或確認資格，請聯絡管理員。",
    },
  ],
} as const satisfies Phase4LandingCopy;

const phase4LandingCopyEn = {
  title: "Maintain a better resume version for each target role.",
  subtitle:
    "Start from one master resume, then create JD-bound target resumes. AI gives suggestions, and you decide what to apply after review.",
  primaryCta: "Use invite account",
  dataNoticeCta: "View data and AI notices",
  signInCta: "Sign in",
  dashboardCta: "Go to dashboard",
  betaSummaryTitle: "Beta access notes",
  betaSummaryIntro: "Access is currently limited to selected invited accounts.",
  betaBullets: [
    {
      id: "1",
      marker: "1",
      text: "Invite sign-in is required. Contact an admin to request access.",
    },
    {
      id: "2",
      marker: "2",
      text: "AI may be unstable. It provides suggestions only, and you review them.",
    },
    {
      id: "3",
      marker: "3",
      text: "Cross-border infrastructure may be involved. Use it only after your own assessment.",
    },
    {
      id: "4",
      marker: "4",
      text: "After trial units run out, package purchase needs further review. Contact an admin.",
    },
  ],
  workflowTitle: "From one master resume to every role target",
  stepLabel: "Step {step}",
  workflow: [
    {
      title: "Create a master resume",
      body: "Upload a PDF or create an empty master while preserving the full factual record.",
    },
    {
      title: "Paste a JD to create a target",
      body: "Each target is bound to one role and optimized around that job description.",
    },
    {
      title: "Review AI suggestions before applying",
      body: "AI provides suggestions only. It does not directly overwrite hard facts, and you confirm each change.",
    },
  ],
  footnoteAriaLabel: "Beta summary notes",
  footnoteLinkLabel: "View note",
  footnotes: [
    {
      id: "1",
      marker: "1",
      body: "This is a controlled beta for selected invited accounts, not a public launch. Account access, purchase eligibility, and usage scale may be limited.",
    },
    {
      id: "2",
      marker: "2",
      body: "AI output may be inaccurate, incomplete, or unsuitable for your real experience. Review it yourself before applying changes.",
    },
    {
      id: "3",
      marker: "3",
      body: "PDFs, resumes, JDs, AI requests, payment records, and support records may be processed through overseas or third-party infrastructure. Continue only if you accept that.",
    },
    {
      id: "4",
      marker: "4",
      body: "After free or trial AI units run out, one-time package purchase still requires account whitelist and manual review. Contact an admin to request or confirm eligibility.",
    },
  ],
} as const satisfies Phase4LandingCopy;

const phase4LicenseDocument: Phase4TrustDocument = {
  title: "License And Attribution",
  description:
    "MultiResume is a hosted product built on an open-source Reactive Resume fork. Open-source licensing and hosted service terms are handled separately.",
  sections: [
    {
      title: "Open-source attribution",
      paragraphs: [
        "MultiResume includes code and design lineage from the open-source Reactive Resume project and preserves the applicable upstream notices in the source distribution.",
        "This attribution does not make the upstream project, its maintainers, issues, documentation, or donation channels the support contact for hosted MultiResume. Contact support@multiresu.me for this service.",
      ],
    },
    {
      title: "MIT license notice",
      paragraphs: [
        "Applicable copyright notices and permission notices are preserved with the relevant source files and distributions.",
        "The MIT License permits use, copying, modification, distribution, sublicensing, and sale of copies, provided the applicable copyright notice and permission notice are included in copies or substantial portions of the software.",
      ],
    },
    {
      title: "Hosted service terms",
      paragraphs: [
        "Open-source software licensing is separate from MultiResume service terms, privacy policy, support operations, quota, payment, and invite-only beta access.",
      ],
    },
  ],
};

export const phase4TrustDocuments: Record<Phase4TrustDocumentKey, Phase4TrustDocument> = {
  terms: {
    title: "用户协议",
    description: "这些条款说明 MultiResume 当前邀请制 beta 的基本使用边界。",
    sections: [
      {
        title: "受控 beta",
        paragraphs: [
          "MultiResume 当前是邀请制 beta。访问、购买和 AI 使用可能受到邀请、白名单、quota、风控和 beta 开放审核限制。",
          "当前 beta 不是公开发布，也不代表长期商业化路径已经确定。",
        ],
      },
      {
        title: "你的内容和责任",
        bullets: [
          "你需要拥有上传简历、PDF、JD 和相关材料的权利。",
          "不要使用 MultiResume 生成虚假、误导、侵权或违法内容。",
          "不要批量抓取、逆向工程、训练竞争性 AI 系统，或上传你无权处理的第三方材料。",
          "AI 输出仅供参考，你负责审核事实、编辑内容和决定最终使用方式。",
        ],
      },
      {
        title: "AI 和求职结果",
        paragraphs: [
          "MultiResume 提供 AI-assisted parsing 和 suggestion-first editing。AI 输出可能不准确、不完整，或不适合你的真实经历。",
          "MultiResume 不提供招聘中介、岗位推荐、雇主匹配、代投递或求职结果保证。",
        ],
      },
      {
        title: "Quota 和支付",
        paragraphs: [
          "MultiResume 是通过互联网交付的数字软件服务（SaaS）和数字无形产品。购买的 AI quota 会在服务端确认支付后发放至你的账号。",
          "当前套装是一次性购买 30 个 AI quota，价格为 29.99 美元（USD）。确认 checkout 时，你仅授权按 checkout 显示的准确金额和币种完成该笔交易；这不会建立订阅、周期扣费或自动续费。",
          "如适用，税费会依据配置的支付安排和适用法律在 checkout 确认前计算并展示；MultiResume 不声称尚未获批的 MoR 或税务身份。",
          "符合资格的付费购买如开启，将通过 Waffo Pancake 提供安全 checkout。卡信息和支付方式明细由 Waffo Pancake checkout 处理，不经过 MultiResume 服务器。",
          "浏览器从 checkout 返回本身不代表支付成功或 quota 已到账；只有服务端确认支付后才会发放 quota，付费 quota 自服务端确认发放时起有效 90 天。",
        ],
        links: [refundLink],
      },
      {
        title: "Support、退款和数据请求",
        id: "refunds",
        headingId: "support",
        paragraphs: [
          `如果你认为某笔扣款有误，请在向银行、发卡机构或支付服务商发起争议前先联系 ${multiResumeBrand.supportEmail}。退款或支付争议请提供账号邮箱、订单号和问题描述；我们会在 2 个工作日内确认收到争议，但不承诺解决或结算时限。`,
          "服务端确认支付后 7 个日历日内，仅当该次付费发放的 quota 完全未被消耗时，才可申请全额退款。若该次发放已有任何 quota 被消耗，通常不退款，适用法律另有强制要求的除外。",
          "经核实的重复扣款由人工处理，并全额退还重复交易；不可放弃的法定权利不受影响。",
        ],
        links: [refundLink, privacyLink],
      },
      {
        title: "运营者、年龄和政策信息",
        paragraphs: [
          "本服务的个人运营者和个人信息处理负责人是 Xi Guanwen，公开地址为中国上海市宝山区南大路38弄9支弄14-201。",
          "本服务仅面向年满18周岁的用户。使用本服务表示你确认自己已年满18周岁，并有权同意这些条款。",
          "创建账号、购买 quota 或使用 MultiResume，即表示你确认已阅读并理解本协议和隐私政策，并同意受其约束。",
          `本页面生效日期为 ${PHASE6_LEGAL_EFFECTIVE_DATE}，政策版本为 ${PHASE6_LEGAL_POLICY_VERSION}。政策变化会在本页面公布；重大变化会通过可用的站内或邮件渠道提示，继续使用前请重新阅读。`,
        ],
      },
      {
        title: "AI provider 和输出限制",
        id: "ai-service",
        paragraphs: [
          `当前配置的 AI provider 是 ${PHASE6_AI_PROVIDER_ENTITY_ZH}，模型为 ${PHASE6_AI_MODEL}，官方 API base 为 ${PHASE6_AI_BASE_URL}。MultiResume 是下游应用运营者和个人信息处理负责人，不代表 DeepSeek，也不表示存在官方合作、认证或背书。`,
          "MultiResume 不会使用简历、JD、用户输入或输出训练或微调自己的模型。DeepSeek 独立在中国大陆处理和存储个人信息；其政策可能允许 provider 端留存、训练或技术优化，并说明退出路径。MultiResume 不承诺 DeepSeek 零留存、不训练或固定留存期限。",
          "不得上传你无权处理的资料，不得将输出当作法律、医疗、财务或求职结果保证；请审核事实并遵守 DeepSeek 条款兼容的安全、合法和不侵权限制。",
        ],
      },
      {
        title: "账号、知识产权和服务边界",
        bullets: [
          "你负责账号凭据的保密、安全使用和账号活动；发现未授权使用时应立即联系 support。",
          "你可以通过 support@multiresu.me 或 privacy@multiresu.me 申请关闭和删除账号。个人信息及需要保留的法律或支付记录将依照隐私政策和适用法律处理。",
          "你保留合法输入材料的权利，并授予 MultiResume 为提供和保护请求功能所必要的处理许可。在你与 MultiResume 之间，MultiResume 将其能够授予的 AI 输出权利全部授予你，但受适用法律和第三方权利限制。输出不保证真实、独创或不侵权；你负责审核事实、权限、授权和发布决定。",
          "服务按现状和可用状态提供，不保证不间断、无错误或适合特定求职目的。",
        ],
      },
      {
        title: "Reactive Resume 开源归属与 MIT 许可",
        id: "open-source",
        paragraphs: [
          "MultiResume 是基于开源 Reactive Resume fork 构建的托管产品。Reactive Resume 上游归属和适用版权通知会随相关源代码保留；这不代表上游维护者负责 MultiResume 的账号、支持或法律问题。",
          "适用源代码按 MIT License 许可。MIT 许可允许使用、复制、修改、分发、再许可和销售副本，但必须在软件副本或主要部分中保留适用的版权和许可通知。",
        ],
      },
      {
        title: "暂停、终止和责任限制",
        paragraphs: [
          "MultiResume 可因安全、滥用、未付款、风控、政策违反或 beta 关闭而暂停或终止访问。除下一段所述由 MultiResume 在用户未违约时永久终止付费服务的情形外，未使用 quota 按退款规则和适用法律处理，不自动承诺现金价值。争议、退款和支付问题请先参阅退款规则并联系 support。",
          "如果 MultiResume 因与你违反本协议无关的原因永久终止付费服务访问，MultiResume 将退还未使用付费 quota 对应的预付金额，但须遵守适用法律和支付服务商的处理限制。",
          "在适用法律允许的最大范围内，MultiResume 对本服务承担的累计责任不超过相关索赔发生前十二个月内你为本服务支付的费用；该限制不排除适用法律不得排除或限制的责任。",
        ],
      },
      {
        title: "通知、完整协议和其他条款",
        paragraphs: [
          "重大政策变化会至少提前30个日历日通过本页面、站内通知或可用邮箱通知；法律、安全或服务紧急变化可能在合理可行时尽快通知。继续使用不代表放弃阅读权利。",
          "这些条款、隐私政策、可接受使用政策和退款规则构成完整协议。若部分条款无效，其余条款继续有效；不主张某项权利不构成放弃。",
        ],
        links: [privacyLink, refundLink],
      },
    ],
  },
  privacy: {
    title: "隐私政策",
    description: "此政策说明 MultiResume 当前 beta 会处理哪些数据，以及你可以如何联系删除、导出或隐私请求。",
    sections: [
      {
        title: "处理的数据",
        bullets: [
          "账号邮箱、invite 状态、purchase whitelist 状态和会话元数据。",
          "PDF 简历、提取文本、结构化简历、JD、用户指令、AI 请求和 AI 输出。",
          "订单、payment event、quota 和 refund/adjustment 元数据，用于支付确认、quota 发放、退款/support 和异常处理。",
          "Waffo Pancake checkout 处理卡信息和支付方式明细；MultiResume 仅保存支付确认、订单、quota 和退款所需的元数据。",
          "Support 消息、问题描述、截图和你主动提供的附件。",
        ],
      },
      {
        title: "AI provider processing",
        paragraphs: [
          "MultiResume 使用第三方 AI provider 生成解析和优化建议。AI 请求可能包含完成该功能所需的简历、JD、当前编辑内容和用户指令。",
          `当前配置的 provider 是 ${PHASE6_AI_PROVIDER_ENTITY_ZH}，模型为 ${PHASE6_AI_MODEL}，官方 API base 为 ${PHASE6_AI_BASE_URL}。DeepSeek 官方隐私政策说明其直接在中国大陆处理和存储个人信息，且输入可能用于改进和训练模型，并提供退出训练或技术优化的权利。MultiResume 是下游控制者，不代表 DeepSeek 或获得其背书。`,
          "MultiResume 不会使用简历、JD、用户输入或输出训练或微调自己的模型。这不改变 DeepSeek 独立的留存、训练、技术优化和退出边界；MultiResume 不承诺 provider 零留存、不训练或固定留存期限。",
        ],
      },
      {
        title: "处理者、共享和必要披露",
        bullets: [
          "Render 用于应用托管；Supabase 用于数据库、认证及相关存储；配置的邮件服务用于账号和支持通信；DeepSeek 用于 AI 请求与输出处理；Waffo Pancake 用于 checkout 和支付处理。",
          "这些必要处理者仅为相应运营目的接收所需数据，并受其适用条款和独立处理边界约束。",
          "MultiResume 可在法律、监管、法院或有权机关要求时，为调查安全事件或欺诈、保护用户/权利/服务，或按用户指示或同意披露信息。",
          "MultiResume 不出售个人信息。",
        ],
      },
      {
        title: "安全事件",
        paragraphs: [
          "MultiResume 会调查符合条件的个人信息或安全事件，并在适用法律要求时通知受影响用户和/或有权机关；在法律不要求时不作固定 72 小时承诺。",
        ],
      },
      {
        title: "跨境处理",
        id: "cross-border",
        paragraphs: [
          "当前 beta 使用境外托管、数据库、对象存储、日志和邮件/support 基础设施；具体区域取决于部署配置，MultiResume 不把它概括为固定或统一区域。DeepSeek 直接在中国大陆处理和存储其接收的信息；Waffo Pancake 是配置中的 checkout/payment 边界，卡信息不会通过或存储在 MultiResume 服务器中。",
          "跨境安全措施包括受限账号访问、身份验证、最小化数据传输、provider 边界和运营日志脱敏；具体供应商合同和区域保障以实际部署及其政策为准。",
          `如需删除、导出、更正、撤回同意或投诉，请联系 ${multiResumeBrand.privacyEmail}。`,
        ],
      },
      {
        title: "Cookie、自动收集和权利请求",
        bullets: [
          "MultiResume 不出售个人信息。产品使用必要的认证/session、语言和主题 cookie；未观察到广告 cookie 或第三方分析 SDK。",
          "自动生成的元数据包括 IP/请求时间、浏览器和设备基本信息、访问路径、request id、认证状态、quota/payment 操作状态及安全/滥用信号；不应把原始简历或完整 prompt 写入普通日志。",
          "你可通过 privacy@multiresu.me 请求访问、导出、更正、删除、限制处理、撤回同意或投诉。我们会在收到可核实请求后以30个日历日为目标回复，但受适用法律和实际系统能力约束。",
          "本服务没有单独任命 DPO；privacy@multiresu.me 由 Xi Guanwen 直接监控并作为隐私联系人。你也可以向你所在地适用的监管或监督机构投诉，MultiResume 不指定或虚构特定机构。",
          "如意外收集未满18岁人士的信息，请通过 privacy@multiresu.me 联系，我们会核验并按适用法律处理删除请求。第三方 provider 的独立政策适用于其自身处理，不能由本政策替代。重大变化至少提前30个日历日通知。",
        ],
      },
      {
        title: "日志和保留",
        bullets: [
          "普通日志不应保存原始简历全文、PDF 原文、完整 prompt 或完整 AI response。",
          "Operational metadata 可用于故障诊断、quota、成本控制、滥用防护和 beta review。",
          "账号、个人资料、用户编写的简历、JD 和 workspace 内容：账号存续期间保留；收到并核实删除请求或账号关闭后，活动系统目标为30天内删除。当前部署没有可验证的统一备份自动过期机制，因此不承诺备份在90天内删除。",
          "暂态原始上传、解析产物和含内容的运营诊断：除非用户保存为账号内容，目标最长30天；安全、滥用和访问日志：180天。",
          "支持、投诉、退款、滥用审查和运营 case：24个月；订单、支付、退款、quota、同意、政策接受和审计记录：7年，法律要求更久时除外。无法合理识别个人的去标识化统计可以不设固定期限。",
          `删除、导出、更正、撤回同意、训练退出和投诉请求请联系 ${multiResumeBrand.privacyEmail}；我们会核验请求人身份或账号权限，并按适用法律和可执行的系统能力处理。`,
        ],
      },
      {
        title: "法律依据和数据主体权利",
        bullets: [
          "提供账号、编辑、导出、AI 和购买服务：履行合同或采取请求的服务前步骤。",
          "AI、跨境处理和购买前政策接受：在需要时基于你的明确同意；你可以撤回同意，但撤回不影响撤回前处理的合法性。",
          "安全、反滥用、故障排查和运营审计：基于合法利益；法定保存、退款和支付记录：基于法律义务或争议处理需要。",
          "在适用范围内，你可以请求访问、复制、符合条件的数据可携带、更正、删除、限制处理、反对处理、撤回同意或投诉；请联系 privacy@multiresu.me，支持问题也可发送至 support@multiresu.me。",
        ],
      },
    ],
  },
  acceptableUsePolicy: {
    title: "可接受使用政策",
    description: "此页面说明当前 beta 允许的用途、禁止行为和执行路径。",
    sections: [
      {
        title: "允许用途",
        bullets: [
          "用于个人简历整理、JD 分析、求职准备和 AI 辅助内容优化。",
          "用于你有权处理的简历、求职材料和相关岗位信息。",
        ],
      },
      {
        title: "禁止行为",
        bullets: [
          "不得伪造履历、学历、证书、工作经历或冒充他人。",
          "不得用于 NSFW 或性剥削内容、暴力或血腥内容、仇恨、CSAM 或任何儿童剥削、深度伪造或冒充、侵犯版权/商标/隐私、欺诈、恶意软件、钓鱼或其他安全滥用。",
          "不得上传你无权处理的第三方简历、JD 或其他资料。",
          "不得批量抓取、逆向工程，或使用本服务训练竞争性 AI 系统。",
        ],
      },
      {
        title: "AI 与 DeepSeek 兼容边界",
        bullets: [
          "不得使用本服务制造虚假履历、冒充他人、侵害隐私或知识产权，或实施违法、欺诈、骚扰、歧视、钓鱼、恶意软件和其他滥用。",
          "不得上传你无权处理的第三方资料，不得批量抓取、逆向工程或用本服务训练竞争性 AI 系统。",
          "DeepSeek 输出可能错误或遗漏，不代表 DeepSeek 观点，也不是法律、医疗、财务或求职结果建议；你必须审核并自行承担使用决定。",
        ],
      },
      {
        title: "AI 输出责任",
        paragraphs: [
          "AI 输出仅供参考。你需要在使用前审核事实、编辑内容并决定最终用途。",
          "如发现误用、滥用或安全问题，请联系 support。MultiResume 可暂停访问、拒绝购买、限制功能或配合调查。",
        ],
      },
      {
        title: "举报和执行",
        paragraphs: [
          "如发现禁止内容、侵权、冒充、儿童安全风险或安全滥用，请联系 support@multiresu.me，提供必要的链接、时间和说明，不要发送不必要的敏感内容。MultiResume 可删除内容、限制功能、暂停或终止账号，并在适用法律允许时配合调查。Waffo 提供的 AIGC 审核页面针对图像/视频场景；MultiResume 是文本简历 AI，不宣称拥有该页面所述的图像/视频审核机制。",
        ],
      },
    ],
  },
  support: {
    title: "支持",
    description: "当前 beta 使用人工支持路径处理账号、AI、quota、支付、退款和数据请求。",
    sections: [
      {
        title: "联系支持",
        paragraphs: [
          `当前支持邮箱：${multiResumeBrand.supportEmail}。请附上你的账号邮箱、发生时间、操作路径和可见 request id 或错误信息。`,
          "请不要主动发送敏感完整简历或身份证明，除非支持人员明确要求。",
        ],
      },
      {
        title: "支持范围",
        bullets: [
          "账号、invite code、purchase whitelist。",
          "PDF 上传、AI 使用、AI 输出质量问题。",
          "Quota、支付未到账、退款、chargeback 或异常订单。",
          "隐私、数据删除、导出、更正、撤回同意和投诉请求。",
        ],
      },
      {
        title: "响应目标",
        paragraphs: ["Beta 阶段的目标是在 2 个工作日内首次回复。此页面不承诺即时客服或 24/7 支持。"],
      },
    ],
  },
  license: phase4LicenseDocument,
  aiService: {
    title: "AI 服务说明",
    description: "此页面说明 MultiResume AI 的建议边界和输入数据。",
    sections: [
      {
        title: "AI 的角色",
        paragraphs: [
          "AI 用于解析、检查和生成优化建议。它不会自动替你投递简历，也不会替你确认事实。",
          "你需要审核、编辑并确认 AI 内容后再应用。",
          "使用 AI 功能时也需要遵守可接受使用政策。",
        ],
      },
      {
        title: "输入范围",
        bullets: [
          "简历结构化数据和必要上下文。",
          "JD、role/company 信息和用户补充指令。",
          "当前 section/item、当前草稿、相关 optimization plan 或局部编辑上下文。",
        ],
      },
      {
        title: "Provider processing",
        bullets: [
          "AI 请求会发送给当前配置的第三方 AI provider。",
          "Provider 的服务主体、模型、处理区域、保留和训练口径以实际 provider 条款和当前配置为准。",
          "MultiResume app 主链路使用境外基础设施，并不代表 AI provider 一定在同一区域处理数据。",
        ],
      },
      {
        title: "限制",
        bullets: [
          "AI 输出可能错误、遗漏、偏差或不适合你的真实经历。",
          "默认不应改写公司、学校、日期、证书、头衔等硬事实。",
          "MultiResume 不保证面试、录用、涨薪、ATS 通过或任何求职结果。",
        ],
      },
    ],
  },
  crossBorderProcessing: {
    title: "跨境数据处理说明",
    description: "此页面说明当前 beta 的境外基础设施和跨境处理可能性。",
    sections: [
      {
        title: "当前 beta 口径",
        paragraphs: [
          "当前 beta 通过境外基础设施提供。用户简历、JD、AI 请求、支付记录和 support 记录可能由境外服务商处理。",
          "这不是公开发布，也不代表数据默认保留在某个固定区域。",
          "Waffo Pancake 处理 checkout 中的卡信息和支付方式明细；MultiResume 仅保存支付、订单和退款相关元数据。",
        ],
      },
      {
        title: "可能涉及的服务",
        bullets: [
          "应用托管、数据库、对象存储、日志和邮件/support 服务。",
          "AI provider、Waffo Pancake checkout，以及支付、订单和退款元数据处理。",
          "用于故障排查、quota 发放、退款/support 和安全防护的运营元数据。",
        ],
      },
      {
        title: "用户选择",
        paragraphs: [
          "如果你不同意跨境处理，请不要上传 PDF、使用 AI 或进入购买流程。你可以通过 support 提出删除、导出、更正、撤回同意或投诉请求。",
        ],
      },
    ],
  },
  refundPolicy: {
    title: "退款规则",
    description: "本规则适用于一次性付费 quota，并与用户协议中的退款资格完全一致。",
    sections: [
      {
        title: "退款资格",
        paragraphs: [
          "服务端确认支付后 7 个日历日内，仅当该次付费发放的 quota 完全未被消耗时，才可申请全额退款。若该次发放已有任何 quota 被消耗，通常不退款，适用法律另有强制要求的除外。",
          "经核实的重复扣款由人工处理，并全额退还重复交易；不可放弃的法定权利不受影响。",
        ],
      },
      {
        title: "优先处理场景",
        bullets: [
          "经核实的重复交易：全额退还重复交易。",
          "支付成功但 quota 未到账。",
          "MultiResume 服务故障导致已购买 quota 无法使用。",
          "Waffo Pancake checkout 或 payment event 状态异常，需要人工核查。",
        ],
      },
      {
        title: "如何提交",
        paragraphs: [
          `请通过 ${multiResumeBrand.supportEmail} 联系 support，并提供账号邮箱、订单号和问题描述。我们会在 2 个工作日内确认收到支付争议，但不承诺解决或结算时限。`,
          `隐私、删除、导出、更正、撤回同意、训练退出或投诉请求也可发送至 ${multiResumeBrand.privacyEmail}。请不要发送不必要的完整简历、身份证明或支付卡数据。`,
        ],
      },
      {
        title: "政策与处理期限",
        paragraphs: [
          `支持、投诉、退款和滥用审查 case 按24个月保留；订单、支付、退款、quota、同意、政策接受和审计记录按7年保留，法律要求更久时除外。账号内容删除请求通过人工支持处理；当前没有可验证的统一备份自动过期机制，因此不会承诺备份90天内删除。`,
        ],
      },
    ],
  },
  betaStatus: {
    title: "Beta 状态说明",
    description: "此页面说明当前 beta 的受控范围和使用限制。",
    sections: [
      {
        title: "当前状态",
        bullets: [
          "邀请制访问。",
          "购买能力只对白名单账号开放。",
          "购买入口可能按 beta review、风控和人工审核状态关闭。",
          "服务通过境外基础设施提供。",
        ],
      },
      {
        title: "默认控制线",
        bullets: [
          "Beta 期限、邀请账号、付费用户、收入、退款和投诉都有受控上限或停止线。",
          "触发停止线后，MultiResume 会暂停扩大 beta，并复盘是否继续开放。",
        ],
      },
      {
        title: "非目标",
        bullets: [
          "只按已批准的境外基础设施提供服务，不新增未确认的基础设施承诺。",
          "不做订阅、优惠券、自动续费、企业版、招聘中介或自动投递。",
        ],
      },
    ],
  },
};

const phase4TraditionalChineseReplacements = [
  ["用户协议", "使用者協議"],
  ["隐私政策", "隱私政策"],
  ["退款规则", "退款規則"],
  ["跨境数据处理说明", "跨境資料處理說明"],
  ["AI 服务说明", "AI 服務說明"],
  ["Beta 状态说明", "Beta 狀態說明"],
  ["数据请求", "資料請求"],
  ["跨境处理", "跨境處理"],
  ["处理的数据", "處理的資料"],
  ["处理区域", "處理區域"],
  ["处理原则", "處理原則"],
  ["用户选择", "使用者選擇"],
  ["服务边界", "服務邊界"],
  ["服务主体", "服務主體"],
  ["服务故障", "服務故障"],
  ["服务说明", "服務說明"],
  ["结构化简历", "結構化履歷"],
  ["原始简历全文", "原始履歷全文"],
  ["完整简历", "完整履歷"],
  ["简历全文", "履歷全文"],
  ["PDF 简历", "PDF 履歷"],
  ["你的简历", "你的履歷"],
  ["当前简历", "目前履歷"],
  ["简历", "履歷"],
  ["用户", "使用者"],
  ["账号", "帳號"],
  ["邀请", "邀請"],
  ["购买", "購買"],
  ["访问", "存取"],
  ["公开", "公開"],
  ["发布", "發布"],
  ["长期", "長期"],
  ["商业化", "商業化"],
  ["路径", "路徑"],
  ["内容", "內容"],
  ["责任", "責任"],
  ["上传", "上傳"],
  ["相关", "相關"],
  ["材料", "資料"],
  ["虚假", "虛假"],
  ["误导", "誤導"],
  ["违法", "違法"],
  ["输出", "輸出"],
  ["仅供参考", "僅供參考"],
  ["负责", "負責"],
  ["审核", "審核"],
  ["编辑", "編輯"],
  ["最终", "最終"],
  ["方式", "方式"],
  ["求职", "求職"],
  ["职位", "職缺"],
  ["岗位", "職缺"],
  ["推荐", "推薦"],
  ["雇主匹配", "雇主配對"],
  ["代投递", "代投遞"],
  ["保证", "保證"],
  ["支付", "支付"],
  ["开启", "開啟"],
  ["实际", "實際"],
  ["名称", "名稱"],
  ["价格", "價格"],
  ["币种", "幣別"],
  ["数量", "數量"],
  ["有效期", "有效期"],
  ["限制", "限制"],
  ["可用", "可用"],
  ["展示", "顯示"],
  ["联系", "聯絡"],
  ["退款", "退款"],
  ["删除", "刪除"],
  ["导出", "匯出"],
  ["阶段", "階段"],
  ["通过", "透過"],
  ["人工", "人工"],
  ["单独判断", "個別判斷"],
  ["政策", "政策"],
  ["哪些", "哪些"],
  ["如何", "如何"],
  ["状态", "狀態"],
  ["会话", "工作階段"],
  ["元数据", "中繼資料"],
  ["提取文本", "擷取文字"],
  ["订单", "訂單"],
  ["用于", "用於"],
  ["确认", "確認"],
  ["发放", "發放"],
  ["异常", "異常"],
  ["消息", "訊息"],
  ["问题描述", "問題描述"],
  ["截图", "截圖"],
  ["主动", "主動"],
  ["提供", "提供"],
  ["第三方", "第三方"],
  ["生成", "產生"],
  ["请求", "請求"],
  ["包含", "包含"],
  ["功能", "功能"],
  ["当前配置", "目前設定"],
  ["配置", "設定"],
  ["固定", "固定"],
  ["明确确认", "明確確認"],
  ["境外", "境外"],
  ["基础设施", "基礎設施"],
  ["记录", "紀錄"],
  ["区域", "區域"],
  ["更正", "更正"],
  ["撤回同意", "撤回同意"],
  ["投诉", "申訴"],
  ["日志", "日誌"],
  ["保留", "保留"],
  ["普通日志", "一般日誌"],
  ["不应", "不應"],
  ["原文", "原文"],
  ["故障诊断", "故障診斷"],
  ["成本控制", "成本控制"],
  ["滥用防护", "濫用防護"],
  ["支持范围", "支援範圍"],
  ["支持", "支援"],
  ["当前", "目前"],
  ["邮箱", "信箱"],
  ["发生时间", "發生時間"],
  ["操作路径", "操作路徑"],
  ["错误信息", "錯誤資訊"],
  ["敏感", "敏感"],
  ["身份证明", "身分證明"],
  ["响应目标", "回覆目標"],
  ["工作日", "工作天"],
  ["首次回复", "首次回覆"],
  ["即时客服", "即時客服"],
  ["界限", "界限"],
  ["角色", "角色"],
  ["检查", "檢查"],
  ["自动", "自動"],
  ["事实", "事實"],
  ["应用", "套用"],
  ["范围", "範圍"],
  ["必要上下文", "必要脈絡"],
  ["补充指令", "補充指令"],
  ["局部", "局部"],
  ["发送给", "傳送給"],
  ["主体", "主體"],
  ["模型", "模型"],
  ["一定", "一定"],
  ["同一区域", "同一區域"],
  ["错误", "錯誤"],
  ["遗漏", "遺漏"],
  ["偏差", "偏差"],
  ["真实经历", "真實經歷"],
  ["默认", "預設"],
  ["改写", "改寫"],
  ["学校", "學校"],
  ["日期", "日期"],
  ["证书", "證書"],
  ["头衔", "職稱"],
  ["面试", "面試"],
  ["录用", "錄用"],
  ["涨薪", "加薪"],
  ["结果", "結果"],
  ["可能涉及", "可能涉及"],
  ["对象存储", "物件儲存"],
  ["邮件", "郵件"],
  ["事件处理", "事件處理"],
  ["如果", "如果"],
  ["不同意", "不同意"],
  ["进入", "進入"],
  ["流程", "流程"],
  ["提交", "提交"],
  ["原则", "原則"],
  ["预期价值", "預期價值"],
  ["明显", "明顯"],
  ["结合", "結合"],
  ["情况", "情況"],
  ["补发", "補發"],
  ["补偿", "補償"],
  ["优先处理", "優先處理"],
  ["重复", "重複"],
  ["成功", "成功"],
  ["到账", "入帳"],
  ["无法", "無法"],
  ["核查", "核查"],
  ["处理方式", "處理方式"],
  ["受控范围", "受控範圍"],
  ["受控", "受控"],
  ["白名单", "白名單"],
  ["入口", "入口"],
  ["风控", "風控"],
  ["控制线", "控制線"],
  ["期限", "期限"],
  ["收入", "收入"],
  ["停止线", "停止線"],
  ["触发", "觸發"],
  ["暂停", "暫停"],
  ["复盘", "複盤"],
  ["是否", "是否"],
  ["继续开放", "繼續開放"],
  ["非目标", "非目標"],
  ["推广", "推廣"],
  ["新增", "新增"],
  ["未确认", "未確認"],
  ["承诺", "承諾"],
  ["优惠券", "優惠券"],
  ["企业版", "企業版"],
  ["招聘中介", "招聘中介"],
] as const;

function toPhase4TraditionalChinese(input: string) {
  return phase4TraditionalChineseReplacements.reduce(
    (value, [source, replacement]) => value.split(source).join(replacement),
    input,
  );
}

function localizePhase4TrustDocumentToTraditionalChinese(document: Phase4TrustDocument): Phase4TrustDocument {
  return {
    title: toPhase4TraditionalChinese(document.title),
    description: toPhase4TraditionalChinese(document.description),
    sections: document.sections.map((section) => ({
      ...(section.id ? { id: section.id } : {}),
      ...(section.headingId ? { headingId: section.headingId } : {}),
      title: toPhase4TraditionalChinese(section.title),
      ...(section.paragraphs
        ? { paragraphs: section.paragraphs.map((paragraph) => toPhase4TraditionalChinese(paragraph)) }
        : {}),
      ...(section.bullets ? { bullets: section.bullets.map((bullet) => toPhase4TraditionalChinese(bullet)) } : {}),
    })),
  };
}

const phase4GeneratedLegacyTrustDocumentsZhTw = Object.fromEntries(
  Object.entries(phase4TrustDocuments).map(([key, document]) => [
    key,
    localizePhase4TrustDocumentToTraditionalChinese(document),
  ]),
) as Record<Phase4TrustDocumentKey, Phase4TrustDocument>;
void phase4GeneratedLegacyTrustDocumentsZhTw;

// Traditional Chinese is an authored policy corpus. It is intentionally not
// produced by transforming the Simplified Chinese copy: legal terminology,
// retention caveats, and provider boundaries are reviewed in this locale.
export const phase4TrustDocumentsZhTw: Record<Phase4TrustDocumentKey, Phase4TrustDocument> = {
  terms: {
    title: "使用者協議",
    description: "本協議說明目前 MultiResume 邀請制 beta 的使用邊界。",
    sections: [
      {
        title: "受控 beta",
        paragraphs: [
          "MultiResume 目前為邀請制 beta。存取、購買及 AI 使用可能受邀請、白名單、quota、風險控制及 beta 審查限制。",
          "此 beta 並非公開上線，也不代表長期商業化方向已確定。服務僅供年滿18歲人士使用；供應的中文地址為官方登記地址拼寫：中國上海市寶山區南大路38弄9支弄14-201。",
          "建立帳號、購買 quota 或使用 MultiResume，即表示你確認已閱讀並理解本協議及隱私政策，並同意受其約束。",
        ],
      },
      {
        title: "內容、帳號與責任",
        bullets: [
          "你必須擁有上傳履歷、PDF、職缺說明及相關資料的權利。你負責帳號憑證保密與帳號活動安全。",
          "你可以透過 support@multiresu.me 或 privacy@multiresu.me 申請關閉及刪除帳號。個人資料及需要保留的法律或付款紀錄將依隱私政策及適用法律處理。",
          "你保留合法輸入資料的權利，並授予 MultiResume 為提供及保護功能所必要的處理許可。在你與 MultiResume 之間，MultiResume 將其能授予的 AI 輸出權利全部授予你，但受適用法律與第三方權利限制。輸出不保證真實、獨創或不侵權；你必須審核事實、權限、授權及使用方式。",
          "不得使用服務製造虛假、誤導、侵權或違法內容；不得批量擷取、逆向工程或訓練競爭性 AI 系統。",
        ],
      },
      {
        title: "Reactive Resume 開源歸屬與 MIT 授權",
        id: "open-source",
        paragraphs: [
          "MultiResume 是基於開源 Reactive Resume fork 建置的託管產品。Reactive Resume 上游歸屬與適用版權通知會隨相關原始碼保留；上游維護者不負責 MultiResume 的帳號、支援或法律問題。",
          "適用原始碼依 MIT License 授權。MIT 授權允許使用、複製、修改、散布、再授權及銷售副本，但必須保留適用的版權與授權通知。",
        ],
      },
      {
        title: "AI 與求職結果",
        paragraphs: [
          "MultiResume 提供 AI 輔助解析及建議優先的編輯。AI 輸出可能不準確、不完整或不適合你的真實經歷。",
          "MultiResume 不提供招聘中介、職缺媒合、代投遞或求職結果保證。",
        ],
      },
      {
        title: "Quota 與付款",
        paragraphs: [
          "MultiResume 是透過網際網路交付的數位軟體服務（SaaS）及數位無形產品。購買的 AI quota 會在伺服器確認付款後發放至你的帳號。",
          "目前套裝是一次性購買 30 個 AI quota，價格為 29.99 美元（USD）。確認 checkout 時，你僅授權按 checkout 顯示的準確金額與幣別完成該筆交易；這不會建立訂閱、週期扣款或自動續費。",
          "如適用，稅費會依設定的付款安排與適用法律在 checkout 確認前計算並顯示；MultiResume 不宣稱尚未獲批准的 MoR 或稅務身分。",
          "符合資格的購買使用 Waffo Pancake 設定中的 test checkout/payment 邊界；live availability、帳戶批准與 MoR 身分仍待外部審查，不作已獲批准的宣稱。卡片與付款方式明細由 Waffo Pancake checkout 處理，不會經過 MultiResume 伺服器。",
          "從 checkout 返回不代表付款成功或 quota 已發放；只有伺服器確認付款後才會發放，付費 quota 自伺服器確認發放時起有效 90 天。",
        ],
        links: [{ label: "退款規則", href: "/refund-policy" }],
      },
      {
        title: "支援、退款與資料請求",
        id: "refunds",
        headingId: "support",
        paragraphs: [
          "如果你認為某筆扣款有誤，請在向銀行、發卡機構或付款服務商提出爭議前先聯絡 support@multiresu.me。退款或付款爭議請提供帳號信箱、訂單號及問題描述；我們會在 2 個工作日內確認收到爭議，但不承諾解決或結算時限。",
          "服務端確認付款後 7 個日曆日內，僅當該次付費發放的 quota 完全未被消耗時，才可申請全額退款。若該次發放已有任何 quota 被消耗，通常不退款，適用法律另有強制要求者除外。",
          "經核實的重複扣款由人工處理，並全額退還重複交易；不可放棄的法定權利不受影響。",
        ],
        links: [{ label: "退款規則", href: "/refund-policy" }, privacyLinkZhTw],
      },
      {
        title: "AI provider 與輸出限制",
        id: "ai-service",
        paragraphs: [
          `目前設定的 AI provider 是 杭州深度求索人工智能基础技术研究有限公司（DeepSeek），模型為 ${PHASE6_AI_MODEL}，官方 API base 為 ${PHASE6_AI_BASE_URL}。MultiResume 是下游應用營運者及控制者，負責下游使用者揭露，不代表 DeepSeek，也不表示有合作、認證或背書。`,
          "MultiResume 不會使用履歷、職缺說明、使用者輸入或輸出訓練或微調自己的模型。DeepSeek 獨立在中國大陸處理及儲存個人資料；其政策可能允許 provider 端保留、訓練或技術最佳化，並說明退出途徑。MultiResume 不承諾 DeepSeek 零保留、不訓練或固定保留期限。",
        ],
      },
      {
        title: "暫停、終止與責任限制",
        paragraphs: [
          "MultiResume 可因安全、濫用、未付款、風控、違反政策或 beta 關閉而暫停或終止存取。除下一段所述由 MultiResume 在使用者未違約時永久終止付費服務的情形外，未使用 quota 依退款規則及適用法律處理，不自動承諾現金價值。",
          "如果 MultiResume 因與你違反本協議無關的原因永久終止付費服務存取，MultiResume 將退還未使用付費 quota 所對應的預付金額，但須遵守適用法律及付款服務商的處理限制。",
          "在適用法律允許的最大範圍內，累計責任不超過索賠前十二個月內你支付的服務費用；適用法律不得排除或限制的責任不受此限制。",
        ],
      },
      {
        title: "通知與完整協議",
        paragraphs: [
          `本頁生效日為 ${PHASE6_LEGAL_EFFECTIVE_DATE}，政策版本為 ${PHASE6_LEGAL_POLICY_VERSION}。重大變更至少提前30個日曆日透過本頁、站內通知或可用信箱通知；法律或安全緊急變更會在合理可行時通知。`,
          "本協議、隱私政策、可接受使用政策及退款規則構成完整協議；部分條款無效時其餘仍有效。",
        ],
        links: [privacyLinkZhTw, { label: "退款規則", href: "/refund-policy" }],
      },
    ],
  },
  privacy: {
    title: "隱私政策",
    description: "本政策說明目前 MultiResume beta 處理的資料，以及如何提出資料權利請求。",
    sections: [
      {
        title: "處理的資料",
        bullets: [
          "帳號信箱、邀請狀態、購買白名單及工作階段中繼資料。",
          "PDF 履歷、擷取文字、結構化履歷、職缺說明、使用者指令、AI 請求及輸出。",
          "訂單、付款事件、quota、退款/調整、同意及稽核中繼資料。",
          "Waffo Pancake checkout 處理卡片與付款方式明細；MultiResume 僅保存確認、支援、quota 及稽核所需的付款資料。",
          "支援訊息、問題描述、截圖及你主動提供的附件。",
        ],
      },
      {
        title: "AI provider 處理",
        paragraphs: [
          `MultiResume 是下游控制者，使用杭州深度求索人工智能基础技术研究有限公司（DeepSeek）的 ${PHASE6_AI_MODEL} 及 ${PHASE6_AI_BASE_URL}，並由 MultiResume 負責終端使用者揭露。DeepSeek 直接在中國大陸處理及儲存個人資料；provider 的 API 條款與隱私政策描述 provider 端處理及合約邊界，不直接管轄 MultiResume 終端使用者。輸入可能用於改進、訓練或技術最佳化，並提供退出訓練或技術最佳化的權利。`,
          "MultiResume 不會使用履歷、職缺說明、使用者輸入或輸出訓練或微調自己的模型。這不改變 DeepSeek 獨立的保留、訓練、技術最佳化及退出邊界；不得宣稱 provider 零保留或不訓練。",
        ],
      },
      {
        title: "處理者、分享與必要揭露",
        bullets: [
          "Render 用於應用託管；Supabase 用於資料庫、認證及相關儲存；設定的郵件服務用於帳號與支援通訊；DeepSeek 用於 AI 請求與輸出；Waffo Pancake 用於 checkout 與付款處理。",
          "必要處理者僅為相應營運目的接收所需資料，並受其適用條款及獨立處理邊界約束。",
          "MultiResume 可在法律、監管、法院或有權機關要求時，為調查安全事件或詐欺、保護使用者/權利/服務，或按使用者指示或同意揭露資料。",
          "MultiResume 不出售個人資料。",
        ],
      },
      {
        title: "安全事件",
        paragraphs: [
          "MultiResume 會調查符合條件的個人資料或安全事件，並在適用法律要求時通知受影響使用者和/或有權機關；在法律不要求時不作固定 72 小時承諾。",
        ],
      },
      {
        title: "跨境處理與保障",
        id: "cross-border",
        paragraphs: [
          "已知主要處理方包括 DeepSeek（中國大陸）與 Waffo Pancake checkout；託管、資料庫、物件儲存、日誌及郵件區域依部署設定而定，不承諾固定區域。卡片資料不會經過或儲存在 MultiResume 伺服器。",
          "已採用的控制包括受限帳號存取、身份驗證、最小化傳輸、provider 邊界及日誌脫敏；實際供應商政策與合約適用。",
        ],
      },
      {
        title: "Cookie、自動收集與權利",
        bullets: [
          "MultiResume 不出售個人資料；使用必要的認證/session、語言及主題 cookie，未觀察到廣告 cookie 或第三方分析 SDK。",
          "自動中繼資料包括 IP/請求時間、瀏覽器與裝置基本資訊、路徑、request id、認證狀態、quota/付款狀態及安全訊號。",
          "在適用範圍內，可透過 privacy@multiresu.me 請求存取、符合條件的資料可攜、更正、刪除、限制處理、反對處理、撤回同意或提出申訴；目標於可核實請求後30個日曆日內回覆，但受適用法律及實際系統能力約束。",
          "未另行任命 DPO；privacy@multiresu.me 由 Xi Guanwen 直接監控。你也可向所在地適用監管或監督機構申訴，MultiResume 不虛構特定機構。",
          "如意外收集未滿18歲人士資料，請聯絡隱私信箱以核驗及依適用法律處理刪除。",
        ],
      },
      {
        title: "保留與刪除目標",
        bullets: [
          "帳號、個人檔案、履歷、職缺及 workspace 內容：帳號有效期間；核實刪除請求或關閉帳號後，活動系統目標於30日內刪除。",
          "暫態上傳/解析產物及含內容診斷目標最長30日；安全、濫用及存取日誌180日；支援、申訴、退款、濫用審查及營運案件24個月。",
          "訂單、付款、退款、quota、同意、政策接受及稽核紀錄保存7年或法律要求更久。去識別化且無法合理識別個人的統計可不限期保存。",
          "目前沒有可驗證的統一備份到期機制，因此不承諾備份90日內刪除。",
        ],
      },
    ],
  },
  acceptableUsePolicy: {
    title: "可接受使用政策",
    description: "本頁說明允許用途、禁止行為、舉報與執行。",
    sections: [
      { title: "允許用途", bullets: ["個人履歷整理、職缺分析、求職準備及 AI 輔助文字改善；只處理你有權處理的資料。"] },
      {
        title: "禁止行為",
        bullets: [
          "不得製造虛假履歷、冒充他人、侵害隱私、版權或商標。",
          "不得製作或傳送 NSFW/性剝削、暴力/血腥、仇恨、CSAM/兒童剝削、深度偽造、欺詐、惡意軟體或釣魚內容。",
          "不得違法、騷擾、歧視、批量擷取、逆向工程或使用服務訓練競爭性 AI。",
        ],
      },
      {
        title: "AI、DeepSeek 與 Waffo 邊界",
        paragraphs: [
          "DeepSeek 輸出可能錯誤，不是法律、醫療、財務或求職建議；請審核並自行負責使用決定。Waffo 引用的 AIGC 審查頁面以圖像/影片為範圍；MultiResume 是文字履歷 AI，不宣稱具備圖像/影片審核機制。",
        ],
      },
      {
        title: "舉報與執行",
        paragraphs: [
          "請將濫用、侵權、冒充、兒童安全或安全問題，以及必要的時間/連結，寄至 support@multiresu.me；不要寄送不必要的敏感資料。MultiResume 可刪除內容、限制功能、暫停或終止帳號，並在適用法律允許時配合調查。",
        ],
      },
    ],
  },
  support: {
    title: "支援",
    description: "目前 beta 以人工途徑處理帳號、AI、quota、付款、退款及資料請求。",
    sections: [
      {
        title: "聯絡支援",
        paragraphs: [
          "支援信箱：support@multiresu.me。請提供帳號信箱、發生時間、操作路徑及可見 request id；除非被要求，請勿寄送完整敏感履歷或身分證明。",
        ],
      },
      {
        title: "支援範圍",
        bullets: [
          "帳號、邀請碼、購買白名單、PDF 上傳、AI 使用及輸出品質。",
          "Quota、付款未入帳、退款、拒付、異常訂單，以及隱私、刪除、匯出、更正、撤回同意及申訴。",
        ],
      },
      {
        title: "回覆說明",
        paragraphs: [
          "beta 階段的人工回覆時間只是非約束性的營運目標，不是 SLA，也不承諾即時或全年無休支援；支援 SLA 仍待外部確認。",
        ],
      },
    ],
  },
  refundPolicy: {
    title: "退款規則",
    description: "本規則適用於一次性付費 quota，並與使用者協議中的退款資格完全一致。",
    sections: [
      {
        title: "退款資格",
        paragraphs: [
          "服務端確認付款後 7 個日曆日內，僅當該次付費發放的 quota 完全未被消耗時，才可申請全額退款。若該次發放已有任何 quota 被消耗，通常不退款，適用法律另有強制要求者除外。",
          "經核實的重複扣款由人工處理，並全額退還重複交易；不可放棄的法定權利不受影響。",
        ],
      },
      {
        title: "提交方式",
        paragraphs: [
          "請寄送帳號信箱、訂單號及問題描述至 support@multiresu.me。我們會在 2 個工作日內確認收到付款爭議，但不承諾解決或結算時限；隱私、刪除、匯出、更正、撤回同意或申訴請寄 privacy@multiresu.me。請勿寄送卡片資料。",
        ],
      },
      {
        title: "保留與備份說明",
        paragraphs: [
          "退款、申訴、支援及濫用審查案件保存24個月；訂單、付款、退款、quota、同意、政策接受及稽核紀錄保存7年或法律要求更久。沒有可驗證的統一備份到期機制，不承諾備份90日內刪除。",
        ],
      },
    ],
  },
  license: phase4LicenseDocument,
  aiService: {
    title: "AI 服務說明",
    description: "MultiResume AI 建議的邊界與輸入資料。",
    sections: [
      {
        title: "角色與限制",
        bullets: [
          "AI 用於解析、檢查及產生建議；你必須審核、編輯並確認內容。",
          "provider 主體、模型、區域、保留及訓練口徑依目前設定與其政策。",
        ],
      },
    ],
  },
  crossBorderProcessing: {
    title: "跨境資料處理說明",
    description: "目前 beta 的跨境處理可能性。",
    sections: [
      {
        title: "目前口徑",
        paragraphs: [
          "DeepSeek 在中國大陸處理及儲存其接收資料；Waffo Pancake 處理 checkout 卡片資料；其他基礎設施區域依部署設定。",
        ],
      },
    ],
  },
  betaStatus: {
    title: "Beta 狀態說明",
    description: "目前 beta 的受控範圍。",
    sections: [{ title: "狀態", bullets: ["邀請制、白名單購買、人工風控及一次性 quota 套裝；不提供訂閱或自動續費。"] }],
  },
};

const phase4TrustDocumentsEn: Record<Phase4TrustDocumentKey, Phase4TrustDocument> = {
  terms: {
    title: "Terms of Service",
    description: "These terms describe the basic boundaries of the current invite-only MultiResume beta.",
    sections: [
      {
        title: "Controlled beta",
        paragraphs: [
          "MultiResume is currently an invite-only beta. Access, purchase, and AI usage may be limited by invitation status, purchase whitelist status, quota, risk controls, and beta review.",
          "This beta is not a public launch and does not determine the long-term commercialization path.",
        ],
      },
      {
        title: "Your content and responsibility",
        bullets: [
          "You must have the right to upload resumes, PDFs, job descriptions, and related materials.",
          "Do not use MultiResume to create false, misleading, infringing, or unlawful content.",
          "Do not bulk extract, reverse engineer, train competing AI systems, or upload third-party materials you are not authorized to process.",
          "AI output is for reference. You are responsible for reviewing facts, editing content, and deciding how to use the final resume.",
        ],
      },
      {
        title: "AI and job-search outcomes",
        paragraphs: [
          "MultiResume provides AI-assisted parsing and suggestion-first editing. AI output can be inaccurate, incomplete, or unsuitable for your real experience.",
          "MultiResume does not provide recruiting agency services, job placement, employer matching, application submission, or job-search outcome promises.",
        ],
      },
      {
        title: "Quota and payment",
        paragraphs: [
          "MultiResume is an internet-delivered digital software service (SaaS) and digital intangible product. Purchased AI quota is delivered to your account after server-side payment confirmation.",
          "The current package is a one-time purchase of 30 AI quota units for $29.99 USD. When you confirm checkout, you authorize only the exact amount and currency shown at checkout for that transaction. This creates no subscription, recurring billing, or automatic renewal.",
          "Applicable taxes, if any, are calculated and shown before checkout confirmation according to the configured payment arrangement and applicable law. MultiResume does not claim an unapproved merchant-of-record or tax status.",
          "Eligible purchases, when enabled, use the configured Waffo Pancake test checkout boundary. Live availability and account approval remain pending external review. Card and payment method details are handled by Waffo Pancake checkout and do not pass through MultiResume servers.",
          "A browser return from checkout does not by itself confirm payment or quota grant. Quota is granted only after server confirmation and expires 90 days after that server-confirmed grant time.",
        ],
        links: [refundLinkEn],
      },
      {
        title: "Support, refunds, and data requests",
        id: "refunds",
        headingId: "support",
        paragraphs: [
          `If you believe a charge is incorrect, contact ${multiResumeBrand.supportEmail} before initiating a dispute with your bank, card issuer, or payment provider. For a refund or billing dispute, provide the account email, order number, and issue description. Billing disputes are acknowledged within two business days; no resolution or settlement time is promised.`,
          "You may request a full refund within seven calendar days after server-side payment confirmation only if none of the quota from that paid grant has been consumed. If any quota from that paid grant was consumed, it is generally non-refundable except where applicable law requires otherwise.",
          "A verified duplicate charge is handled manually and the duplicate transaction is fully refunded. Non-waivable statutory rights remain unaffected.",
        ],
        links: [refundLinkEn, privacyLinkEn],
      },
      {
        title: "Operator, age, and policy information",
        paragraphs: [
          `The individual operator and personal information controller for this service is Xi Guanwen. The public address is ${multiResumeBrand.legalAddress}.`,
          "The service is for people aged 18 and over. By using it, you confirm that you are at least 18 and have authority to accept these Terms.",
          "By creating an account, purchasing quota, or using MultiResume, you confirm that you have read and understood these Terms and the Privacy Policy and agree to be bound by them.",
          `This page is effective ${PHASE6_LEGAL_EFFECTIVE_DATE} under policy version ${PHASE6_LEGAL_POLICY_VERSION}. Changes will be posted here; material changes may also be notified through available in-product or email channels. Review the updated policy before continuing to use the service.`,
        ],
      },
      {
        title: "AI provider and output boundaries",
        id: "ai-service",
        paragraphs: [
          `The currently configured AI provider is ${PHASE6_AI_PROVIDER_ENTITY}, using ${PHASE6_AI_MODEL} through the official API base ${PHASE6_AI_BASE_URL}. MultiResume is the downstream application operator and controller; it is not DeepSeek and does not claim an official partnership, certification, or endorsement.`,
          "MultiResume does not train or fine-tune its own models on resumes, job descriptions, user inputs, or outputs. DeepSeek independently processes and stores personal information in the People's Republic of China; its policies may permit provider-side retention, training, or technology optimization and describe an opt-out path. MultiResume does not promise zero provider retention, provider no-training, or a fixed provider retention period.",
          "Do not submit material you are not authorized to process. Do not use outputs as legal, medical, financial, or job-outcome advice; review facts and follow restrictions compatible with DeepSeek's terms on lawful, safe, and non-infringing use.",
        ],
      },
      {
        title: "Accounts, intellectual property, and service limits",
        bullets: [
          "You are responsible for credential confidentiality, account security, and activity under your account; contact support promptly about unauthorized use.",
          "You may request account closure and deletion through support@multiresu.me or privacy@multiresu.me. Personal information and retained legal or payment records are handled according to the Privacy Policy and applicable law.",
          "You retain rights in lawful input materials and grant MultiResume the limited license needed to provide and protect the requested feature. As between you and MultiResume, MultiResume grants you all rights it can grant in outputs generated for you, subject to applicable law and third-party rights. Outputs are not promised to be original, truthful, or non-infringing; you must review facts, authority, permissions, and publication decisions.",
          "The service is provided as-is and as-available, without a promise of uninterrupted, error-free, or job-specific results.",
        ],
      },
      {
        title: "Reactive Resume attribution and MIT license",
        id: "open-source",
        paragraphs: [
          "MultiResume is a hosted product built on an open-source Reactive Resume fork. Reactive Resume attribution and applicable copyright notices remain with the relevant source distribution; the upstream maintainers do not operate MultiResume support or legal channels.",
          "The applicable source is distributed under the MIT License. MIT permits use, copying, modification, distribution, sublicensing, and sale of copies when the applicable copyright and permission notices remain in the software or substantial portions.",
        ],
      },
      {
        title: "Suspension, termination, and liability",
        paragraphs: [
          "MultiResume may suspend or terminate access for safety, abuse, non-payment, risk controls, policy violations, or beta closure. Except for the merchant-initiated non-breach termination rule below, unused quota is handled under the Refund Policy and applicable law and has no automatic cash value. Review the Refund Policy and contact Support for billing, refund, or dispute questions.",
          "If MultiResume permanently terminates access to a paid service for reasons unrelated to your breach of these Terms, MultiResume will refund the prepaid amount attributable to unused paid quota, subject to applicable law and payment-provider processing constraints.",
          "To the maximum extent permitted by law, MultiResume's aggregate liability for the service is limited to the fees you paid for the service during the twelve months before the claim. This does not limit liability that applicable law does not permit to be excluded or limited.",
        ],
      },
      {
        title: "Notices and complete agreement",
        paragraphs: [
          "Material policy changes will be posted and notified at least 30 calendar days in advance through this page, in-product notice, or available email. Legal or security emergencies may be notified as soon as reasonably practicable.",
          "These Terms, the Privacy Policy, Acceptable Use Policy, and Refund Policy are the complete agreement for this service. If a provision is invalid, the remaining provisions continue; failure to enforce a right is not a waiver.",
        ],
        links: [privacyLinkEn, refundLinkEn],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    description:
      "This policy explains what data the current MultiResume beta processes and how to contact us for privacy, deletion, or export requests.",
    sections: [
      {
        title: "Data we process",
        bullets: [
          "Account email, invite status, purchase whitelist status, and session metadata.",
          "PDF resumes, extracted text, structured resumes, job descriptions, user instructions, AI requests, and AI outputs.",
          "Order, payment event, quota, and refund or adjustment metadata for payment confirmation, quota grant, refund/support, and exception handling.",
          "Waffo Pancake checkout handles card and payment method details. MultiResume stores only the payment, order, quota, and refund metadata needed for confirmation, support, and audit.",
          "Support messages, issue descriptions, screenshots, and attachments you choose to provide.",
        ],
      },
      {
        title: "AI provider processing",
        paragraphs: [
          "MultiResume uses third-party AI providers to generate parsing and optimization suggestions. AI requests may include the resume, JD, current editing content, and user instructions needed to provide the feature.",
          `The current provider is ${PHASE6_AI_PROVIDER_ENTITY}, using ${PHASE6_AI_MODEL} at ${PHASE6_AI_BASE_URL}. DeepSeek's official privacy policy says it directly processes and stores personal information in the People's Republic of China, may use inputs to improve and train models, and provides a right to opt out of training or technology optimization. MultiResume is the downstream controller and does not claim DeepSeek endorsement.`,
          "MultiResume does not train or fine-tune its own models on resumes, job descriptions, user inputs, or outputs. This does not change DeepSeek's independent retention, training, technology-optimization, or opt-out boundary. MultiResume does not promise zero provider retention, provider no-training, or a fixed provider retention period.",
        ],
      },
      {
        title: "Processors, sharing, and required disclosures",
        bullets: [
          "Render provides application hosting; Supabase provides database, authentication, and related storage; the configured email service sends account and support communications; DeepSeek processes AI requests and outputs; Waffo Pancake provides checkout and payment processing.",
          "These processors receive only data necessary for their stated operational purposes and act under their own applicable terms and processing boundaries.",
          "MultiResume may disclose information where required by law, regulation, court order, or competent authority; to investigate security incidents or fraud; to protect users, rights, or the service; or at the user's direction or consent.",
          "MultiResume does not sell personal information.",
        ],
      },
      {
        title: "Security incidents",
        paragraphs: [
          "MultiResume will investigate qualifying personal-data or security incidents and notify affected users and/or competent authorities where applicable law requires. No fixed 72-hour promise is made where it is not legally required.",
        ],
      },
      {
        title: "Cross-border processing",
        id: "cross-border",
        paragraphs: [
          "Known principal processing boundaries include DeepSeek, which directly processes and stores information in the People's Republic of China, and Waffo Pancake checkout for card and payment-method details. Hosting, database, object storage, logging, and email/support regions depend on deployment configuration; MultiResume does not promise a fixed region. Card data does not pass through or reside on MultiResume servers.",
          "Operational safeguards include restricted account access, authentication, data minimization, provider boundaries, and redacted operational logs. Specific provider contracts and regional safeguards depend on the deployed configuration.",
          `For deletion, export, correction, consent withdrawal, or privacy complaints, contact ${multiResumeBrand.privacyEmail}.`,
        ],
      },
      {
        title: "Cookies, automatic metadata, and rights",
        bullets: [
          "MultiResume does not sell personal information. It uses essential authentication/session, language, and theme cookies; no advertising cookie or third-party analytics SDK has been observed in the product.",
          "Automatically collected metadata includes IP/request time, basic browser and device information, route, request id, authentication state, quota/payment operation state, and security or abuse signals. Routine logs should not contain full resumes or prompts.",
          "You may request access, export, correction, deletion, restriction, consent withdrawal, or complaint handling at privacy@multiresu.me. We target a response within 30 calendar days after a verifiable request, subject to applicable law and executable system capability.",
          "No separate DPO has been appointed; privacy@multiresu.me is monitored directly by Xi Guanwen as the privacy contact. You may also complain to an applicable supervisory authority in your jurisdiction; MultiResume does not invent or designate a specific authority.",
          "If information from someone under 18 is accidentally collected, contact the privacy address for verification and deletion under applicable law. Third-party policies govern their own processing and are not replaced by this policy. Material changes are notified at least 30 calendar days in advance.",
        ],
      },
      {
        title: "Logs and retention",
        bullets: [
          "Routine logs should not store full raw resumes, full PDF text, full prompts, or full AI responses.",
          "Operational metadata may be used for troubleshooting, quota, cost control, abuse prevention, and beta review.",
          "Account, profile, user-authored resume, JD, and workspace content is retained while the account is active. After a verified deletion request or account closure, the target is deletion from active systems within 30 days. The current deployment has no verified, uniform backup-expiry mechanism, so MultiResume does not promise backup deletion within 90 days.",
          "Transient raw uploads, parse artifacts, and content-bearing operational diagnostics have a target maximum of 30 days unless saved as account content. Security, abuse, and access logs are retained for 180 days.",
          "Support, complaint, refund, abuse-review, and operator case records are retained for 24 months. Order, payment, refund, quota, consent, policy-acceptance, and audit records are retained for 7 years, or longer where law requires. De-identified aggregate statistics may be kept without a fixed limit when they cannot reasonably identify a person.",
          `For deletion, export, correction, consent withdrawal, training opt-out, or complaints, contact ${multiResumeBrand.privacyEmail}. We verify the requester and respond according to applicable law and the capabilities the deployment can actually execute.`,
        ],
      },
      {
        title: "Legal bases and data-subject rights",
        bullets: [
          "Account, editing, export, AI, and purchase services: performance of a contract or steps requested before a contract.",
          "AI, cross-border processing, and checkout policy acceptance: explicit consent where required. You may withdraw consent; withdrawal does not affect processing already carried out lawfully.",
          "Security, abuse prevention, troubleshooting, and operational audit: legitimate interests. Statutory payment, refund, consent, and audit records: legal obligations or dispute handling.",
          "Where applicable, you may request access, a copy, qualified data portability, correction, deletion, restriction, object to processing, withdraw consent, or request complaint handling through privacy@multiresu.me. Support issues may also be sent to support@multiresu.me.",
        ],
      },
    ],
  },
  acceptableUsePolicy: {
    title: "Acceptable Use Policy",
    description:
      "This page describes allowed use, prohibited conduct, and enforcement boundaries for the current beta.",
    sections: [
      {
        title: "Allowed use",
        bullets: [
          "Personal resume maintenance, JD analysis, job-search preparation, and AI-assisted content improvement.",
          "Materials that you are authorized to upload and process.",
        ],
      },
      {
        title: "Prohibited conduct",
        bullets: [
          "Fabricating credentials, employment history, education, licenses, or impersonating another person.",
          "NSFW or sexual exploitation, violence or gore, hate, CSAM or child exploitation, deepfake or impersonation, copyright, trademark or privacy infringement, fraud, malware, phishing, illegal, harassing, discriminatory, or other abusive use.",
          "Uploading third-party resumes, JDs, or related materials without authorization.",
          "Bulk extraction, reverse engineering, or using the service to train competing AI systems.",
        ],
      },
      {
        title: "AI and DeepSeek-compatible boundaries",
        bullets: [
          "Do not use the service to fabricate resumes, impersonate people, infringe privacy or intellectual property, or commit illegal, fraudulent, harassing, discriminatory, phishing, malware, or other abusive conduct.",
          "Do not upload third-party material without authorization, bulk extract, reverse engineer, or use the service to train a competing AI system.",
          "DeepSeek outputs may contain errors or omissions, do not represent DeepSeek's views, and are not legal, medical, financial, or job-outcome advice. Review outputs and take responsibility for decisions based on them.",
        ],
      },
      {
        title: "Review and enforcement",
        paragraphs: [
          "AI output is a suggestion tool. You remain responsible for reviewing facts, editing content, and deciding how to use the final result.",
          "Report misuse, abuse, or security issues through support. MultiResume may suspend access, refuse purchases, limit features, or assist with investigations.",
        ],
      },
      {
        title: "Reporting and enforcement",
        paragraphs: [
          "Report prohibited content, infringement, impersonation, child-safety concerns, or security abuse to support@multiresu.me with only the necessary link, time, and description. MultiResume may remove content, limit features, suspend or terminate accounts, and cooperate with investigations where permitted by law. Waffo's cited AIGC compliance page is image/video-scoped; MultiResume is text-resume AI and does not claim those image/video moderation mechanisms.",
        ],
      },
    ],
  },
  support: {
    title: "Support",
    description:
      "The current beta uses a manual support path for account, AI, quota, payment, refund, and data requests.",
    sections: [
      {
        title: "Contact support",
        paragraphs: [
          `Current support email: ${multiResumeBrand.supportEmail}. Please include your account email, time of occurrence, operation path, and any visible request id or error message.`,
          "Please do not proactively send sensitive full resumes or identity documents unless support asks for them.",
        ],
      },
      {
        title: "Support scope",
        bullets: [
          "Account, invite code, and purchase whitelist.",
          "PDF upload, AI usage, and AI output quality issues.",
          "Quota, missing payment credit, refund, chargeback, or abnormal order states.",
          "Privacy, data deletion, export, correction, consent withdrawal, and complaint requests.",
        ],
      },
      {
        title: "Response target",
        paragraphs: [
          "During beta, any manual response timing is a non-binding operational aim, not a support SLA. This page does not promise instant or 24/7 support; a support SLA remains subject to external Operations confirmation.",
        ],
      },
    ],
  },
  license: phase4LicenseDocument,
  aiService: {
    title: "AI Service Notice",
    description: "This page explains MultiResume AI suggestion boundaries and input data.",
    sections: [
      {
        title: "Role of AI",
        paragraphs: [
          "AI is used for parsing, checking, and generating optimization suggestions. It does not submit applications for you or confirm facts on your behalf.",
          "You need to review, edit, and confirm AI content before applying it.",
          "Use of AI features is also subject to the Acceptable Use Policy.",
        ],
      },
      {
        title: "Input scope",
        bullets: [
          "Structured resume data and necessary context.",
          "JD, role/company information, and user instructions.",
          "Current section/item, current draft, related optimization plan, or local editing context.",
        ],
      },
      {
        title: "Provider processing",
        bullets: [
          "AI requests are sent to the currently configured third-party AI provider.",
          "The provider service entity, model, processing region, retention, and training posture depend on the actual provider terms and current configuration.",
          "The app's overseas infrastructure does not mean AI provider processing necessarily uses the same region.",
        ],
      },
      {
        title: "Limitations",
        bullets: [
          "AI output can contain errors, omissions, bias, or content unsuitable for your real experience.",
          "By default, hard facts such as companies, schools, dates, credentials, and titles should not be rewritten.",
          "MultiResume does not promise interviews, offers, salary increases, ATS outcomes, or any job-search result.",
        ],
      },
    ],
  },
  crossBorderProcessing: {
    title: "Cross-border Data Processing",
    description: "This page explains the current beta's overseas infrastructure and possible cross-border processing.",
    sections: [
      {
        title: "Current beta posture",
        paragraphs: [
          "The current beta is provided through overseas infrastructure. User resumes, JDs, AI requests, payment records, and support records may be processed by overseas providers.",
          "This is not a public launch and does not mean data stays within any fixed region by default.",
          "Waffo Pancake handles card and payment method details during checkout. MultiResume stores only payment, order, and refund metadata required for confirmation, support, audit, and quota handling.",
        ],
      },
      {
        title: "Services that may be involved",
        bullets: [
          "Application hosting, database, object storage, logging, email, and support services.",
          "AI provider, Waffo Pancake checkout, and payment, order, or refund metadata processing.",
          "Operational metadata used for troubleshooting, quota grants, refunds/support, and security controls.",
        ],
      },
      {
        title: "User choice",
        paragraphs: [
          "If you do not agree to cross-border processing, do not upload PDFs, use AI, or enter the purchase flow. You can contact support for deletion, export, correction, consent withdrawal, or complaint requests.",
        ],
      },
    ],
  },
  refundPolicy: {
    title: "Refund Policy",
    description:
      "This policy applies to one-time paid quota grants and matches the refund terms in the Terms of Service.",
    sections: [
      {
        title: "Eligibility",
        paragraphs: [
          "You may request a full refund within seven calendar days after server-side payment confirmation only if none of the quota from that paid grant has been consumed. If any quota from that paid grant was consumed, it is generally non-refundable except where applicable law requires otherwise.",
          "A verified duplicate charge is handled manually and the duplicate transaction is fully refunded. Non-waivable statutory rights remain unaffected.",
        ],
      },
      {
        title: "Priority scenarios",
        bullets: [
          "Verified duplicate transaction: full refund of the duplicate transaction.",
          "Payment succeeded but quota was not granted.",
          "MultiResume service fault prevents use of purchased quota.",
          "Waffo Pancake checkout or payment event state is abnormal and needs manual review.",
        ],
      },
      {
        title: "How to request help",
        paragraphs: [
          `Contact ${multiResumeBrand.supportEmail} with your account email, order number, and issue description. Billing disputes are acknowledged within two business days; no resolution or settlement time is promised.`,
          `Privacy, deletion, export, correction, consent withdrawal, training opt-out, or complaint requests may also be sent to ${multiResumeBrand.privacyEmail}. Do not send a full resume, identity document, or card data unless it is necessary and support requests it.`,
        ],
      },
      {
        title: "Policy and record periods",
        paragraphs: [
          `Support, complaint, refund, and abuse-review cases are retained for 24 months. Order, payment, refund, quota, consent, policy-acceptance, and audit records are retained for 7 years, or longer where law requires. Account-content deletion requests are handled through manual support; because no uniform backup-expiry mechanism is verified, MultiResume does not promise backup deletion within 90 days.`,
        ],
      },
    ],
  },
  betaStatus: {
    title: "Beta Status",
    description: "This page explains the current beta scope and usage limits.",
    sections: [
      {
        title: "Current status",
        bullets: [
          "Invite-only access.",
          "Purchase access is limited to whitelisted accounts.",
          "The purchase entry can be closed by beta review, risk-control, or manual review status.",
          "The service is provided through overseas infrastructure.",
        ],
      },
      {
        title: "Default control lines",
        bullets: [
          "Beta duration, invite accounts, paid users, revenue, refunds, and complaints all have controlled limits or stop lines.",
          "If a stop line is triggered, MultiResume will pause beta expansion and review whether to continue opening access.",
        ],
      },
      {
        title: "Non-goals",
        bullets: [
          "Use only the approved overseas infrastructure, without adding unconfirmed infrastructure commitments.",
          "No subscriptions, coupons, auto-renewal, enterprise plan, recruiting agency service, or automatic application submission.",
        ],
      },
    ],
  },
};

export const forbiddenPhase4PositiveClaims = [
  "MultiResume 已经正式公开上线",
  "本地合规上线完成",
  "支付宝 / 微信支付已支持",
  "官方 DeepSeek 合作伙伴",
  "DeepSeek 认证",
  "承诺零数据留存",
  "承诺不会用于训练",
  "承诺数据默认不出境",
  "保证你获得面试",
  "保证你获得录用",
  "保证你获得涨薪",
  "保证你通过 ATS",
  "Alipay is supported",
  "WeChat Pay is supported",
  "official DeepSeek partner",
  "zero data retention",
  "not used for training",
  "guaranteed interview",
  "guaranteed hiring",
] as const;

export function getPhase4DisclosureCopy(surface: Phase4DisclosureSurface) {
  if (isMultiResumeEnglishLocale()) return phase4DisclosuresEn[surface];
  if (isMultiResumeTraditionalChineseLocale()) return phase4DisclosuresZhTw[surface];

  return phase4Disclosures[surface];
}

export function getPhase4LandingCopy() {
  if (isMultiResumeEnglishLocale()) return phase4LandingCopyEn;
  if (isMultiResumeTraditionalChineseLocale()) return phase4LandingCopyZhTw;

  return phase4LandingCopy;
}

export function getPhase4TrustDocument(key: Phase4TrustDocumentKey) {
  if (isMultiResumeEnglishLocale()) return phase4TrustDocumentsEn[key];
  if (isMultiResumeTraditionalChineseLocale()) return phase4TrustDocumentsZhTw[key];

  return phase4TrustDocuments[key];
}

function getPhase4TrustCopyStrings(
  disclosures: Record<Phase4DisclosureSurface, Phase4DisclosureCopy>,
  landingCopy: Phase4LandingCopy,
  trustDocuments: Record<Phase4TrustDocumentKey, Phase4TrustDocument>,
  trustLinks: Phase4TrustLink[],
) {
  const disclosureStrings = Object.values(disclosures).flatMap((disclosure) => [
    disclosure.title,
    disclosure.body,
    ...disclosure.bullets,
    ...disclosure.requiredAcknowledgements,
    ...disclosure.links.flatMap((link) => [link.label, link.href]),
  ]);

  const landingStrings = [
    landingCopy.title,
    landingCopy.subtitle,
    landingCopy.primaryCta,
    landingCopy.dataNoticeCta,
    landingCopy.signInCta,
    landingCopy.dashboardCta,
    landingCopy.betaSummaryTitle,
    landingCopy.betaSummaryIntro,
    ...landingCopy.betaBullets.flatMap((item) => [item.id, item.marker, item.text]),
    landingCopy.workflowTitle,
    landingCopy.stepLabel,
    ...landingCopy.workflow.flatMap((item) => [item.title, item.body]),
    landingCopy.footnoteAriaLabel,
    landingCopy.footnoteLinkLabel,
    ...landingCopy.footnotes.flatMap((item) => [item.id, item.marker, item.body]),
  ];

  const documentStrings = Object.values(trustDocuments).flatMap((document) => [
    document.title,
    document.description,
    ...document.sections.flatMap((section) => [
      section.title,
      ...(section.paragraphs ?? []),
      ...(section.bullets ?? []),
    ]),
  ]);

  return [...disclosureStrings, ...landingStrings, ...documentStrings, ...trustLinks.map((link) => link.label)];
}

export function getAllPhase4TrustCopyStrings() {
  return [
    ...getPhase4TrustCopyStrings(phase4Disclosures, phase4LandingCopy, phase4TrustDocuments, phase4TrustLinks),
    ...getPhase4TrustCopyStrings(phase4Disclosures, phase4LandingCopyZhTw, phase4TrustDocumentsZhTw, phase4TrustLinks),
    ...getPhase4TrustCopyStrings(phase4DisclosuresEn, phase4LandingCopyEn, phase4TrustDocumentsEn, phase4TrustLinksEn),
  ];
}
